import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { pushParams, resetParams } from 'dpl/actions/query_params';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import Toggle from 'dpl/common/design_system/Toggle';
import { FILTER_SIMILAR_BREEDS } from 'dpl/constants/query_params';
import useSimilarBreedFilter from 'dpl/puppy_search_results/hooks/useSimilarBreedFilter';

import Divider from './Divider';

const TOGGLE_ID = 'similar-breeds-toggle';
const TITLE = 'Show more puppies from similar breeds';
const DESCRIPTION =
  'Similar size, coat, and temperament, from breeders near you.';

const PLACEHOLDER_TEXT_CLASS_NAME = 'bg-gray-200 bg-loading-animation br1';

// Renders the real copy as transparent text so the placeholder wraps like it.
function SimilarBreedsFilterSkeleton() {
  return (
    <div aria-hidden="true">
      <SkeletonBlock
        className="font-20 lh-title mb6"
        width="8em"
        height="1.25em"
      />
      <div className="flex items-center gap-12">
        <SkeletonBlock
          className="flex-none br-pill"
          width="40px"
          height="24px"
        />
        <div style={{ color: 'transparent' }}>
          <span
            className={`db font-16 fw-medium ${PLACEHOLDER_TEXT_CLASS_NAME}`}
            style={{ width: 'fit-content' }}
          >
            {TITLE}
          </span>
          <span className={`db font-16 mt1 ${PLACEHOLDER_TEXT_CLASS_NAME}`}>
            {DESCRIPTION}
          </span>
        </div>
      </div>
      <Divider />
    </div>
  );
}

export default function SimilarBreedsFilter() {
  const dispatch = useDispatch();
  const isSelected = useSelector(({ queryParams }: any) =>
    Boolean(queryParams[FILTER_SIMILAR_BREEDS])
  );
  const { isFilterVisible, isFilterPending } = useSimilarBreedFilter();

  // Reset, not set to `false`: `Boolean('false')` is true.
  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      dispatch(pushParams({ [FILTER_SIMILAR_BREEDS]: true }, { hash: true }));
    } else {
      dispatch(resetParams(FILTER_SIMILAR_BREEDS, { hash: true }));
    }
  }

  if (isFilterPending) {
    return <SimilarBreedsFilterSkeleton />;
  }

  if (!isFilterVisible) {
    return null;
  }

  return (
    <>
      <h4 className="font-20 lh-title mb6">Similar breeds</h4>
      <div className="flex items-center gap-12">
        <Toggle
          className="flex-none"
          id={TOGGLE_ID}
          checked={isSelected}
          onChange={handleToggleChange}
          dataTestId={TOGGLE_ID}
        />
        <label htmlFor={TOGGLE_ID} className="pointer">
          <span className="db font-16 fw-medium">{TITLE}</span>
          <span className="db font-16 secondary mt1">{DESCRIPTION}</span>
        </label>
      </div>
      <Divider />
    </>
  );
}
